/**
 * XSS Sanitizer Utility
 *
 * Client-side XSS prevention using DOMPurify.
 * Sanitizes user input before submission to remove malicious scripts and HTML.
 *
 * @module xss-sanitizer
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize a single input string to prevent XSS attacks
 *
 * Removes:
 * - All HTML tags (except safe formatting like <b>, <i>, <em>, <strong>)
 * - All script tags and event handlers
 * - Potentially dangerous attributes
 *
 * @param input - User input string (potentially unsafe)
 * @returns Sanitized string (safe for submission)
 *
 * @example
 * ```typescript
 * const unsafe = '<script>alert("XSS")</script>Hello';
 * const safe = sanitizeInput(unsafe);
 * // Result: 'Hello'
 *
 * const unsafe2 = '<img src=x onerror=alert(1)>';
 * const safe2 = sanitizeInput(unsafe2);
 * // Result: '<img src="x">'
 * ```
 */
export function sanitizeInput(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  try {
    // Configure DOMPurify for strict text sanitization
    const config = {
      // Allow only safe formatting tags
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'p'],

      // Allow only safe attributes
      ALLOWED_ATTR: [],

      // Forbid all event handlers
      FORBID_ATTR: [
        'onclick',
        'onload',
        'onerror',
        'onmouseover',
        'onmouseout',
        'onmouseenter',
        'onmouseleave',
        'onfocus',
        'onblur',
        'onchange',
        'oninput',
        'onsubmit',
        'onkeydown',
        'onkeyup',
        'onkeypress',
        'ontouchstart',
        'ontouchend',
        'ontouchmove',
        'onpointerdown',
        'onpointerup',
        'onpointermove',
      ],

      // Forbid dangerous tags
      FORBID_TAGS: [
        'script',
        'iframe',
        'embed',
        'object',
        'link',
        'style',
        'meta',
        'base',
        'form',
        'input',
        'button',
        'select',
        'textarea',
      ],

      // Keep text content even if tags are removed
      KEEP_CONTENT: true,

      // Return string (not DOM)
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
    };

    // Sanitize input
    const sanitized = DOMPurify.sanitize(input, config);

    return sanitized;
  } catch (error) {
    console.error('[XSS Sanitizer] Failed to sanitize input:', error);
    // Return empty string on error (fail-safe)
    return '';
  }
}

/**
 * Sanitize form data to prevent XSS attacks
 *
 * Processes all string fields in form data and sanitizes them.
 * Non-string fields are preserved as-is.
 *
 * @param data - Form data object (potentially unsafe)
 * @returns Sanitized form data (safe for submission)
 *
 * @example
 * ```typescript
 * const formData = {
 *   title: '<script>alert("XSS")</script>Bug Title',
 *   description: 'Normal description',
 *   email: 'user@example.com',
 *   count: 42
 * };
 *
 * const sanitized = sanitizeFormData(formData);
 * // Result:
 * // {
 * //   title: 'Bug Title',
 * //   description: 'Normal description',
 * //   email: 'user@example.com',
 * //   count: 42
 * // }
 * ```
 */
export function sanitizeFormData<T extends Record<string, any>>(data: T): T {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sanitized = { ...data };

  for (const key in sanitized) {
    if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
      const value = sanitized[key];

      // Sanitize string fields
      if (typeof value === 'string') {
        sanitized[key] = sanitizeInput(value) as any;
      }
      // Recursively sanitize nested objects
      else if (value && typeof value === 'object' && !Array.isArray(value)) {
        sanitized[key] = sanitizeFormData(value) as any;
      }
      // Sanitize array elements (if strings)
      else if (Array.isArray(value)) {
        sanitized[key] = value.map((item: any) =>
          typeof item === 'string' ? sanitizeInput(item) : item
        ) as any;
      }
      // Preserve non-string primitives (numbers, booleans, null, undefined)
    }
  }

  return sanitized;
}

/**
 * Sanitize specific form fields
 *
 * Useful when you only want to sanitize specific fields instead of the entire form.
 *
 * @param data - Form data object
 * @param fields - Array of field names to sanitize
 * @returns Form data with sanitized fields
 *
 * @example
 * ```typescript
 * const formData = {
 *   title: '<script>alert("XSS")</script>Bug Title',
 *   description: 'Normal description',
 *   internalNotes: '<admin>Important</admin>', // Don't sanitize this
 * };
 *
 * const sanitized = sanitizeFields(formData, ['title', 'description']);
 * // Result:
 * // {
 * //   title: 'Bug Title',
 * //   description: 'Normal description',
 * //   internalNotes: '<admin>Important</admin>', // Preserved
 * // }
 * ```
 */
export function sanitizeFields<T extends Record<string, any>>(
  data: T,
  fields: (keyof T)[]
): T {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sanitized = { ...data };

  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(sanitized, field)) {
      const value = sanitized[field];

      if (typeof value === 'string') {
        sanitized[field] = sanitizeInput(value) as any;
      }
    }
  }

  return sanitized;
}

/**
 * Validate that a string does not contain XSS patterns
 *
 * Returns true if the string is safe (no XSS detected).
 * Returns false if XSS patterns are detected.
 *
 * @param input - String to validate
 * @returns True if safe, false if XSS detected
 *
 * @example
 * ```typescript
 * const safe = isInputSafe('Normal text');
 * // Result: true
 *
 * const unsafe = isInputSafe('<script>alert("XSS")</script>');
 * // Result: false
 * ```
 */
export function isInputSafe(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return true; // Empty input is safe
  }

  // Sanitize and compare with original
  const sanitized = sanitizeInput(input);

  // If sanitized version is significantly different, input was unsafe
  // Allow for minor differences in whitespace normalization
  const originalNormalized = input.trim().replace(/\s+/g, ' ');
  const sanitizedNormalized = sanitized.trim().replace(/\s+/g, ' ');

  return originalNormalized === sanitizedNormalized;
}

/**
 * Strip all HTML tags from a string
 *
 * More aggressive than sanitizeInput - removes ALL HTML tags.
 *
 * @param input - String with HTML tags
 * @returns Plain text without any HTML
 *
 * @example
 * ```typescript
 * const html = '<p>Hello <b>world</b>!</p>';
 * const text = stripHtml(html);
 * // Result: 'Hello world!'
 * ```
 */
export function stripHtml(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  try {
    // Configure DOMPurify to remove all tags
    const config = {
      ALLOWED_TAGS: [],      // No tags allowed
      ALLOWED_ATTR: [],      // No attributes allowed
      KEEP_CONTENT: true,    // Keep text content
    };

    const stripped = DOMPurify.sanitize(input, config);
    return stripped;
  } catch (error) {
    console.error('[XSS Sanitizer] Failed to strip HTML:', error);
    return '';
  }
}
